import React, { useMemo } from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import type { PresaveFansSummary } from 'fym-shared/src/smartlinks/presaveSubscriptions';
import { TypedMemo } from '../../common/types';
import { fansMessages as messages } from './fansMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet per screen; each part of the screen uses its own slice of it */
import styles from './Fans.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface FansTotalsRowProps {
  summary: PresaveFansSummary;
}

/**
 * The three totals: subscribers, new in the period, need to reconnect (design c1). "Need to
 * reconnect" uses the warn colour only when the number is above 0. On Free the new fans line says
 * that sign-ups are closed (design c6).
 */
const FansTotalsRowComponent: React.FC<FansTotalsRowProps> = ({ summary }) => {
  const intl = useIntl();
  const left = useMemo(() => summary.series.reduce((sum, day) => sum + day.unsubscribed, 0), [summary.series]);

  return (
    <div className={styles.totals}>
      <div className={styles.totalCard}>
        <div className={styles.totalKey}>{intl.formatMessage(messages.totalLabel)}</div>
        <div className={styles.totalValue} data-testid="fans-total">
          {intl.formatNumber(summary.total)}
        </div>
        <div className={styles.totalHint}>{intl.formatMessage(messages.totalHint)}</div>
      </div>
      <div className={styles.totalCard}>
        <div className={styles.totalKey}>{intl.formatMessage(messages.newLabel, { days: summary.days })}</div>
        <div className={styles.totalValue} data-testid="fans-new">
          {intl.formatNumber(summary.newInPeriod)}
        </div>
        <div className={styles.totalHint}>
          {summary.canUsePresave
            ? intl.formatMessage(messages.newLeft, { count: intl.formatNumber(left) })
            : intl.formatMessage(messages.newClosed)}
        </div>
      </div>
      <div className={clsx(styles.totalCard, styles.totalReauth, summary.needsReauth > 0 && styles.totalWarn)}>
        <div className={styles.totalKey}>{intl.formatMessage(messages.reauthLabel)}</div>
        <div className={styles.totalValue} data-testid="fans-needs-reauth">
          {intl.formatNumber(summary.needsReauth)}
        </div>
        <div className={styles.totalHint}>{intl.formatMessage(messages.reauthHint)}</div>
      </div>
    </div>
  );
};

export const FansTotalsRow = TypedMemo(FansTotalsRowComponent);
